import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { FaCalendarAlt } from 'react-icons/fa';
import { ToastContainer } from 'react-toastify';
import { useTitle } from '../../../shared/context/TitleContext';
import { apiGet } from '../../../shared/utils/apiConfig';
import { showErrorToast } from '../../../shared/utils/toastHelper';

const dayNamesAr = {
  saturday: 'السبت',
  sunday: 'الأحد',
  monday: 'الإثنين',
  tuesday: 'الثلاثاء',
  wednesday: 'الأربعاء',
  thursday: 'الخميس',
  friday: 'الجمعة'
};

function formatDay(key) {
  if (!key) return '-';
  const lower = String(key).toLowerCase();
  return dayNamesAr[lower] || key;
}

const isActiveDay = (item) => {
  if (item == null) return false;
  if (typeof item.is_active !== 'undefined') return Boolean(item.is_active);
  if (typeof item.active !== 'undefined') return Number(item.active) === 1;
  if (typeof item.status !== 'undefined') return String(item.status).toLowerCase() === 'active';
  return false;
};

const DoctorClinicWorkingDays = () => {
  const navigate = useNavigate();
  const { setTitle } = useTitle();

  const [clinics, setClinics] = useState([]);
  const [selectedClinicId, setSelectedClinicId] = useState('');
  const [workingDays, setWorkingDays] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingClinics, setLoadingClinics] = useState(false);
  const [activeTab, setActiveTab] = useState('active'); // 'active' | 'inactive'

  useEffect(() => {
    setTitle('أيام العمل - العيادات');
  }, [setTitle]);

  useEffect(() => {
    let isMounted = true;
    const fetchClinics = async () => {
      try {
        setLoadingClinics(true);
        const data = await apiGet('/doctor/my_clinics');
        if (!isMounted) return;
        const list = data?.result || data?.data || [];
        setClinics(Array.isArray(list) ? list : []);
        if (Array.isArray(list) && list.length > 0) {
          setSelectedClinicId(String(list[0].id ?? list[0].clinic_id ?? ''));
        }
      } catch (err) {
        if (!isMounted) return;
        showErrorToast('فشل تحميل العيادات');
      } finally {
        if (isMounted) {
          setLoadingClinics(false);
        }
      }
    };
    fetchClinics();
    return () => {
      isMounted = false;
    };
  }, []);

  useEffect(() => {
    let isMounted = true;
    const fetchWorkingDays = async () => {
      if (!selectedClinicId && activeTab === 'active') return;
      try {
        setLoading(true);
        let data;
        if (activeTab === 'active') {
          data = await apiGet('/doctor_dash/doctor_work_days', { clinic_id: selectedClinicId });
        } else {
          data = await apiGet('/doctor_dash/empty_work_days');
        }
        if (!isMounted) return;
        const list = data?.result || data?.data || [];
        setWorkingDays(Array.isArray(list) ? list : []);
      } catch (err) {
        if (!isMounted) return;
        showErrorToast('فشل تحميل أيام العمل');
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };
    fetchWorkingDays();
    return () => {
      isMounted = false;
    };
  }, [selectedClinicId, activeTab]);

  const displayDays = workingDays;

  return (
    <div className="container-fluid">
      <ToastContainer />
      <div className="d-flex justify-content-between align-items-center my-4">
        <h5 className="mb-0">أيام العمل - العيادات</h5>
      </div>

      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="card mb-4">
        <div className="card-body">
          <div className="row g-3 align-items-end">
            <div className="col-12">
              <label className="form-label">اختر العيادة</label>
              <select
                className="form-select"
                value={selectedClinicId}
                onChange={(e) => setSelectedClinicId(e.target.value)}
                disabled={loadingClinics}
              >
                <option value="">
                  {loadingClinics ? 'جاري التحميل...' : 'جميع العيادات'}
                </option>
                {clinics.map((c) => (
                  <option key={c.id ?? c.clinic_id} value={String(c.id ?? c.clinic_id)}>
                    {c.title || c.name || c.clinic_name || `عيادة رقم ${c.id ?? c.clinic_id}`}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div>
      </motion.div>

      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="card mb-4 workdays-tabs">
        <div className="card-body">
          <ul className="nav nav-pills justify-content-center" role="tablist">
            <li className="nav-item" role="presentation">
              <button
                className={`nav-link ${activeTab === 'active' ? 'active' : ''}`}
                onClick={() => setActiveTab('active')}
                type="button"
                role="tab"
              >
                أيام نشطة
              </button>
            </li>
            <li className="nav-item" role="presentation">
              <button
                className={`nav-link ${activeTab === 'inactive' ? 'active' : ''}`}
                onClick={() => setActiveTab('inactive')}
                type="button"
                role="tab"
              >
                أيام غير نشطة
              </button>
            </li>
          </ul>
        </div>
      </motion.div>

      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className={`card ${activeTab === 'inactive' ? 'inactive-days-card' : ''}`}>
        <div className="card-body">
          {loading ? (
            <div className="text-center py-4">
              <div className="spinner-border text-primary" role="status">
                <span className="visually-hidden">جاري التحميل...</span>
              </div>
            </div>
          ) : displayDays.length === 0 ? (
            <div className="text-center py-5">
              <p className="text-muted mb-0">لا توجد بيانات</p>
            </div>
          ) : (
            <div className={`table-responsive ${activeTab === 'inactive' ? 'inactive-days-wrap' : ''}`}>
              <table className={`table table-modern table-working-days ${activeTab === 'inactive' ? 'table-inactive-days' : ''}`}>
                <thead>
                  <tr>
                    {activeTab === 'active' ? (
                      <>
                        <th>#</th>
                        <th>اليوم</th>
                        <th>من</th>
                        <th>إلى</th>
                        <th>الحالة</th>
                      </>
                    ) : (
                      <>
                        <th>#</th>
                        <th>اليوم</th>
                      </>
                    )}
                  </tr>
                </thead>
                <tbody>
                  {displayDays.map((d, idx) => (
                    <tr key={idx}>
                      {activeTab === 'active' ? (
                        <>
                          <td>{idx + 1}</td>
                          <td>{formatDay(d.day || d.name)}</td>
                          <td>{d.from || d.start || d.from_time || '-'}</td>
                          <td>{d.to || d.end || d.to_time || '-'}</td>
                          <td>
                            <span className="badge" style={{ backgroundColor: '#28a745', color: '#fff' }}>نشط</span>
                          </td>
                        </>
                      ) : (
                        <>
                          <td>{idx + 1}</td>
                          <td>
                            <div style={{width : "200px"}} className="badge inactive-day-badge">{formatDay(d.day || d.name)}</div>
                          </td>
                        </>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </motion.div>

      <style jsx>{`
        .workdays-tabs .nav-pills {
          gap: 0.5rem;
        }
        .workdays-tabs .nav-pills .nav-link {
          color: rgb(24, 65, 148);
          border: 1px solid rgba(24, 65, 148, 0.25);
          background-color: rgba(24, 65, 148, 0.06);
          border-radius: 999px;
          padding: 0.5rem 1rem;
          transition: all 0.2s ease-in-out;
          font-weight: 600;
        }
        .workdays-tabs .nav-pills .nav-link:hover {
          background-color: rgba(24, 65, 148, 0.12);
          border-color: rgba(24, 65, 148, 0.35);
        }
        .workdays-tabs .nav-pills .nav-link.active {
          background-color: rgb(24, 65, 148);
          border-color: rgb(24, 65, 148);
          color: #fff;
          box-shadow: 0 0.25rem 0.5rem rgba(24, 65, 148, 0.25);
        }
        
        .table-working-days {
          font-size: 0.85rem;
          color: #000;
        }
        
        .table-working-days th,
        .table-working-days td {
          padding: 0.3rem 0.4rem;
          font-size: 0.75rem;
          white-space: nowrap;
          color: #000 !important;
        }
        
        .table-working-days th:nth-child(1),
        .table-working-days td:nth-child(1) {
          width: 50px;
          min-width: 50px;
          max-width: 50px;
        }
        
        .table-working-days td:nth-child(2) {
          text-align: center;
          min-width: 80px;
          max-width: 100px;
          width: 90px;
        }
        
        .table-working-days th:nth-child(3),
        .table-working-days td:nth-child(3),
        .table-working-days th:nth-child(4),
        .table-working-days td:nth-child(4) {
          width: 80px;
          min-width: 80px;
          max-width: 100px;
        }
        
        .table-working-days th:nth-child(5),
        .table-working-days td:nth-child(5) {
          width: 70px;
          min-width: 70px;
          max-width: 80px;
        }
        
        .table-working-days td:nth-child(4) {
          color: #000 !important;
        }
        
        .table-working-days td:nth-child(4) .badge {
          color: #fff !important;
        }
        
        .table-working-days td:nth-child(3) {
          color: #000 !important;
        }
        
        @media (max-width: 768px) {
          .table-working-days td:nth-child(2) {
            min-width: 70px;
            max-width: 90px;
          }
        }

        /* Inactive days width constraints (scoped to this page only) */
        .inactive-days-card .inactive-days-wrap {
          display: block;
        }
        .inactive-days-card .table-inactive-days {
          width: 100%;
          margin: 0;
        }
        .inactive-days-card .table-inactive-days th,
        .inactive-days-card .table-inactive-days td {
          padding: 0.3rem 0.4rem;
          font-size: 0.75rem;
          white-space: nowrap;
          color: #000 !important;
        }
        
        .inactive-days-card .table-inactive-days th:nth-child(1),
        .inactive-days-card .table-inactive-days td:nth-child(1) {
          width: 50px;
          min-width: 50px;
          max-width: 50px;
        }
        
        .inactive-days-card .table-inactive-days th:nth-child(2),
        .inactive-days-card .table-inactive-days td:nth-child(2) {
          text-align: center;
          min-width: 80px;
          max-width: 100px;
          width: 90px;
        }
        
        /* use default thead font sizes like active */
        
        .inactive-days-card .inactive-day-badge {
          background: #28a745;
          color: #fff !important;
          padding: 0.25rem 0.5rem;
          font-weight: 600;
          font-size: 0.7rem;
          border-radius: 0.5rem;
          display: inline-block;
          width: auto;
        }
      `}</style>
    </div>
  );
};

export default DoctorClinicWorkingDays;



